import { useContext } from "react";

import GlobalContext from "shared/providers/context/GlobalContext";
import { User } from "shared/providers/context/types/auth-context";

import * as Sentry from "@sentry/nextjs";

import {
  IInitiateCheckoutPayload,
  IPayload,
  IPurchasePayload,
} from "./index.d";
import { dispatchAPITrack } from "./services";
import {
  getAdvancedMatching,
  getPixelData,
  getTrackData,
  isDisabled,
} from "./utils";

export const usePixel = () => {
  const { state } = useContext(GlobalContext);

  const tenantData = state?.tenancy;
  const globalPixels = tenantData?.pixels;

  const OPTIONS = {
    autoConfig: true,
    debug: false,
  };

  const getRandomID = () => {
    try {
      const characters =
        "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
      const length = 8;

      const randomID = Array.from({ length }, () =>
        characters.charAt(Math.floor(Math.random() * characters.length)),
      ).join("");
      return randomID;
    } catch (error) {
      Sentry.captureException(error);
      Sentry.captureMessage("Error generating pixel eventId");
      return null;
    }
  };

  async function getFbCookie(name: string): Promise<string | undefined> {
    await new Promise((resolve) => setTimeout(() => resolve(null), 1000));

    const cookies = document.cookie.split(";");

    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split("=");
      if (cookieName.trim() === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return undefined;
  }

  const setTrackPageView = async (loggedUser: User): Promise<void> => {
    try {
      const pixels = globalPixels?.length ? [...globalPixels] : [];

      const advancedMatching = getAdvancedMatching(loggedUser);

      const ReactPixel = (
        await import("@matheuscampos_450/react-facebook-pixel")
      )?.default;

      pixels?.forEach((pixelItem) => {
        const { type, pixel_code } = pixelItem;

        ReactPixel?.init(pixel_code, advancedMatching, OPTIONS);

        if (type === "facebook") ReactPixel?.pageView();
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      Sentry.captureMessage("Error Pixel setTrackPageView");
    }
  };

  const setTrackViewContent = async (
    payload: IPayload,
    loggedUser: User,
  ): Promise<void> => {
    try {
      const fbc = await getFbCookie("_fbc");
      const fbp = await getFbCookie("_fbp");

      if (!!fbc) {
        payload.fbc = fbc;
      }
      if (!!fbp) {
        payload.fbp = fbp;
      }

      const { slug, className, pixelResponse, promoterLink } = payload;

      const data = await getPixelData(
        pixelResponse,
        className,
        slug,
        promoterLink,
      );
      const pixels = !promoterLink
        ? globalPixels?.length
          ? [...globalPixels, ...data]
          : data
        : data;

      const viewContentProps = getTrackData(payload);
      const advancedMatching = getAdvancedMatching(loggedUser);

      const ReactPixel = (
        await import("@matheuscampos_450/react-facebook-pixel")
      )?.default;

      pixels?.forEach((pixelItem) => {
        const {
          type,
          pixel_code,
          track_view_content,
          conversion_api_enabled,
          id,
        } = pixelItem;

        ReactPixel?.init(pixel_code, advancedMatching, OPTIONS);

        if (!track_view_content) return;

        if (type === "facebook") {
          const event_id = getRandomID();

          ReactPixel?.pageView();
          ReactPixel?.trackSingle(pixel_code, "ViewContent", viewContentProps, {
            eventID: event_id,
          });

          if (conversion_api_enabled)
            dispatchAPITrack("ViewContent", event_id, id, loggedUser, payload);
        }
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      Sentry.captureMessage("Error Pixel setTrackViewContent");
    }
  };

  const setTrackInitiateCheckout = async (
    payload: IInitiateCheckoutPayload,
    loggedUser: User,
  ): Promise<void> => {
    try {
      const fbc = await getFbCookie("_fbc");
      const fbp = await getFbCookie("_fbp");

      if (!!fbc) {
        payload.fbc = fbc;
      }
      if (!!fbp) {
        payload.fbp = fbp;
      }
      const { slug, className, pixelResponse, promoterLink } = payload;
      const data = await getPixelData(
        pixelResponse,
        className,
        slug,
        promoterLink,
      );
      const pixels = !promoterLink
        ? globalPixels?.length
          ? [...globalPixels, ...data]
          : data
        : data;
      const initiateCheckoutProps = getTrackData(payload);
      const advancedMatching = getAdvancedMatching(loggedUser);

      const ReactPixel = (
        await import("@matheuscampos_450/react-facebook-pixel")
      ).default;
      pixels?.forEach((pixelItem) => {
        const { type, pixel_code, track_checkout, conversion_api_enabled, id } =
          pixelItem;
        ReactPixel?.init(pixel_code, advancedMatching, OPTIONS);
        if (!track_checkout) return;
        if (type === "facebook") {
          const event_id = getRandomID();

          ReactPixel?.trackSingleCustom(
            pixel_code,
            "InitiateCheckout",
            initiateCheckoutProps,
            { eventID: event_id },
          );
          if (conversion_api_enabled)
            dispatchAPITrack(
              "InitiateCheckout",
              event_id,
              id,
              loggedUser,
              payload,
            );
        }
      });
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      Sentry.captureMessage("Error Pixel setTrackInitiateCheckout");
    }
  };

  const setTrackPurchase = async (
    payload: IPurchasePayload,
    loggedUser: User,
    optionalEvent = "Purchase",
  ): Promise<void> => {
    const fbc = await getFbCookie("_fbc");
    const fbp = await getFbCookie("_fbp");

    if (!!fbc) {
      payload.fbc = fbc;
    }
    if (!!fbp) {
      payload.fbp = fbp;
    }
    try {
      const { slug, className, pixelResponse, promoterLink, paymentMethod } =
        payload;

      const data = await getPixelData(
        pixelResponse,
        className,
        slug,
        promoterLink,
      );

      const pixels = !promoterLink
        ? globalPixels?.length
          ? [...globalPixels, ...data]
          : data
        : data;

      const purchaseProps = getTrackData(payload);
      const advancedMatching = getAdvancedMatching(loggedUser);

      const ReactPixel = (
        await import("@matheuscampos_450/react-facebook-pixel")
      )?.default;

      pixels?.map((pixelItem) => {
        const { type, pixel_code } = pixelItem;
        ReactPixel?.init(pixel_code, advancedMatching, OPTIONS);

        if (isDisabled(pixelItem, paymentMethod)) return;

        if (type === "facebook") {

          ReactPixel?.trackSingle(pixel_code, optionalEvent, purchaseProps, {
            eventID: payload.event_id,
          });
        }
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      Sentry.captureMessage("Error Pixel setTrackPurchase");
    }
  };

  return {
    setTrackPageView,
    setTrackViewContent,
    setTrackInitiateCheckout,
    setTrackPurchase,
  };
};

export * from "./index.d";
